import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Phone, Mail, Building2, Calendar, Search,
  Send, CheckCircle, Clock, XCircle, RefreshCw,
  ChevronDown, ChevronUp, ExternalLink, CreditCard,
  BarChart3, Filter, Eye, MessageSquare, Server, TrendingUp, Rocket, Grid3X3, Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { HighTicketDashboard } from './HighTicketDashboard';
import { ClientSitesManager } from './ClientSitesManager';
import { CreateClientSiteModal } from './CreateClientSiteModal';
import { PremiumDemosGallery } from './PremiumDemosGallery';
import { DemoRentalManager } from './DemoRentalManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'Nuevo', color: 'bg-blue-500', icon: Clock },
  contacted: { label: 'Contactado', color: 'bg-yellow-500', icon: Phone },
  quoted: { label: 'Presupuestado', color: 'bg-purple-500', icon: Send },
  converted: { label: 'Convertido', color: 'bg-emerald-500', icon: CheckCircle },
  lost: { label: 'Perdido', color: 'bg-red-500', icon: XCircle }
};

// Business type labels
const BUSINESS_TYPES = {
  peluqueria: 'Peluquería',
  estetica: 'Centro de Estética',
  limpieza: 'Limpieza',
  fontanero: 'Fontanería',
  electricista: 'Electricista',
  cerrajero: 'Cerrajería',
  taller: 'Taller Mecánico',
  detailing: 'Car Detailing',
  electrodomesticos: 'Electrodomésticos',
  panaderia: 'Panadería',
  humedades: 'Humedades',
  jardineria: 'Jardinería',
  reformas: 'Reformas',
  climatizacion: 'Climatización',
  piscinas: 'Piscinas',
  otro: 'Otro'
};

export function AdminWebsSolicitudesPanel({ adminId, isSuperAdmin = false }) {
  // Tab state
  const [activeTab, setActiveTab] = useState('galeria');
  
  const [solicitudes, setSolicitudes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', businessType: '', search: '' });
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showCreateSiteModal, setShowCreateSiteModal] = useState(false);
  const [siteCreationSolicitud, setSiteCreationSolicitud] = useState(null);
  const [quoteForm, setQuoteForm] = useState({
    quote_amount: '',
    quote_description: '',
    include_payment_link: true,
    notes_internal: ''
  });
  const [sendingQuote, setSendingQuote] = useState(false);

  // Fetch solicitudes
  const fetchSolicitudes = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.businessType) params.append('business_type', filter.businessType);
      
      const response = await axios.get(`${API_URL}/api/solicitud-web?${params}`);
      let data = response.data.solicitudes || [];
      
      // Apply search filter locally
      if (filter.search) {
        const search = filter.search.toLowerCase();
        data = data.filter(s => 
          s.business_name?.toLowerCase().includes(search) ||
          s.contact_name?.toLowerCase().includes(search) ||
          s.phone?.includes(search) ||
          s.email?.toLowerCase().includes(search)
        );
      }
      
      setSolicitudes(data);
    } catch (error) {
      console.error('Error fetching solicitudes:', error);
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  }, [filter.status, filter.businessType, filter.search]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/solicitud-web/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchSolicitudes();
    fetchStats();
  }, [fetchSolicitudes, fetchStats]);

  // Update status
  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/api/solicitud-web/${id}?status=${status}`);
      toast.success('Estado actualizado');
      fetchSolicitudes();
      fetchStats();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // Send quote
  const sendQuote = async (e) => {
    e.preventDefault();
    if (!selectedSolicitud || !quoteForm.quote_amount || !quoteForm.quote_description) {
      toast.error('Completa todos los campos requeridos');
      return;
    }

    try {
      setSendingQuote(true);
      await axios.post(`${API_URL}/api/solicitud-web/send-quote`, {
        solicitud_id: selectedSolicitud.id,
        quote_amount: parseFloat(quoteForm.quote_amount),
        quote_description: quoteForm.quote_description,
        include_payment_link: quoteForm.include_payment_link,
        notes_internal: quoteForm.notes_internal
      });
      
      toast.success('Presupuesto enviado correctamente');
      setShowQuoteModal(false);
      setQuoteForm({ quote_amount: '', quote_description: '', include_payment_link: true, notes_internal: '' });
      setSelectedSolicitud(null);
      fetchSolicitudes();
      fetchStats();
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar presupuesto');
    } finally {
      setSendingQuote(false);
    }
  };

  // Open quote modal
  const openQuoteModal = (solicitud) => {
    if (!solicitud.email) {
      toast.error('Este lead no tiene email registrado');
      return;
    }
    setSelectedSolicitud(solicitud);
    setShowQuoteModal(true);
  };

  // Handle site creation success
  const handleSiteCreated = (site) => {
    fetchSolicitudes();
    fetchStats();
    setShowCreateSiteModal(false);
    setSiteCreationSolicitud(null);
    // Switch to sites tab
    setActiveTab('sites');
  };

  // Open site creation modal for a solicitud
  const openCreateSiteModal = (solicitud) => {
    setSiteCreationSolicitud(solicitud);
    setShowCreateSiteModal(true);
  };

  // Tab definitions
  const tabs = [
    { id: 'galeria', label: 'Galería Demos', icon: Grid3X3 },
    { id: 'rental', label: 'Rank & Rent', icon: Settings },
    { id: 'solicitudes', label: 'Solicitudes', icon: MessageSquare },
    { id: 'sites', label: 'Sitios Desplegados', icon: Server },
    { id: 'high-ticket', label: 'High Ticket', icon: TrendingUp }
  ];

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">Webs Demo</h2>
            <p className="text-gray-400 text-sm mt-1">Gestiona solicitudes, despliega sitios y analiza conversiones</p>
          </div>
          <div className="flex items-center gap-3">
            <a 
              href={`${process.env.REACT_APP_BACKEND_URL}/api/webs/`}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Ver Demos
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-gray-800 pb-2">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-cyan-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
                data-testid={`webs-tab-${tab.id}`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'galeria' && (
        <PremiumDemosGallery />
      )}

      {activeTab === 'rental' && (
        <DemoRentalManager />
      )}

      {activeTab === 'high-ticket' && (
        <HighTicketDashboard adminId={adminId} />
      )}

      {activeTab === 'sites' && (
        <ClientSitesManager 
          adminId={adminId} 
          onCreateSite={() => setActiveTab('solicitudes')}
        />
      )}

      {activeTab === 'solicitudes' && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <StatsCard 
                label="Total" 
                value={stats.total} 
                icon={<BarChart3 className="w-5 h-5" />}
                color="bg-gray-700"
              />
              <StatsCard 
                label="Nuevos" 
                value={stats.by_status?.new || 0} 
                icon={<Clock className="w-5 h-5" />}
                color="bg-blue-600"
              />
              <StatsCard 
                label="Contactados" 
                value={stats.by_status?.contacted || 0} 
                icon={<Phone className="w-5 h-5" />}
                color="bg-yellow-600"
              />
              <StatsCard 
                label="Presupuestados" 
                value={stats.by_status?.quoted || 0} 
                icon={<Send className="w-5 h-5" />}
                color="bg-purple-600"
              />
              <StatsCard 
                label="Convertidos" 
                value={stats.by_status?.converted || 0} 
                icon={<CheckCircle className="w-5 h-5" />}
                color="bg-emerald-600"
              />
            </div>
          )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, teléfono o email..."
              value={filter.search}
              onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <select
            value={filter.status}
            onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            value={filter.businessType}
            onChange={(e) => setFilter(prev => ({ ...prev, businessType: e.target.value }))}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
          >
            <option value="">Todos los sectores</option>
            {Object.entries(BUSINESS_TYPES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Solicitudes List */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-gray-400">Cargando solicitudes...</p>
          </div>
        ) : solicitudes.length === 0 ? (
          <div className="p-12 text-center">
            <Globe className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No hay solicitudes que mostrar</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {solicitudes.map(solicitud => (
              <SolicitudRow
                key={solicitud.id}
                solicitud={solicitud}
                onUpdateStatus={updateStatus}
                onSendQuote={() => openQuoteModal(solicitud)}
                onDeploySite={() => openCreateSiteModal(solicitud)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quote Modal */}
      {showQuoteModal && selectedSolicitud && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800 rounded-2xl max-w-lg w-full p-6"
          >
            <h3 className="text-xl font-bold text-white mb-2">Enviar Presupuesto</h3>
            <p className="text-gray-400 text-sm mb-6">
              Para <strong className="text-white">{selectedSolicitud.business_name}</strong> - {selectedSolicitud.email}
            </p>
            
            <form onSubmit={sendQuote} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Importe (€) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={quoteForm.quote_amount}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, quote_amount: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  placeholder="497"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Descripción del presupuesto *
                </label>
                <textarea
                  value={quoteForm.quote_description}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, quote_description: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 resize-none"
                  placeholder="Web profesional de alta conversión para tu peluquería. Incluye: diseño personalizado, formulario de contacto, optimización móvil, SEO básico, dominio y hosting 1 año."
                  required
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="include_payment_link"
                  checked={quoteForm.include_payment_link}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, include_payment_link: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
                />
                <label htmlFor="include_payment_link" className="text-gray-300 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Incluir enlace de pago Stripe
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Notas internas (opcional)
                </label>
                <input
                  type="text"
                  value={quoteForm.notes_internal}
                  onChange={(e) => setQuoteForm(prev => ({ ...prev, notes_internal: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  placeholder="Ej: Cliente referido, urgente, etc."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowQuoteModal(false); setSelectedSolicitud(null); }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={sendingQuote}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {sendingQuote ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Enviar Presupuesto
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
        </>
      )}

      {/* Create Site Modal */}
      <AnimatePresence>
        {showCreateSiteModal && siteCreationSolicitud && (
          <CreateClientSiteModal
            solicitud={siteCreationSolicitud}
            adminId={adminId}
            onClose={() => {
              setShowCreateSiteModal(false);
              setSiteCreationSolicitud(null);
            }}
            onSuccess={handleSiteCreated}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Stats Card Component
function StatsCard({ label, value, icon, color }) {
  return (
    <div className={`${color} rounded-xl p-4`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/70 text-xs">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
        <div className="text-white/50">{icon}</div>
      </div>
    </div>
  );
}

// Solicitud Row Component
function SolicitudRow({ solicitud, onUpdateStatus, onSendQuote, onDeploySite }) {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_CONFIG[solicitud.status] || STATUS_CONFIG.new;
  const StatusIcon = statusConfig.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-gray-800/50 hover:bg-gray-800 transition-colors">
      <div 
        className="p-4 flex items-center gap-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Status Badge */}
        <div className={`${statusConfig.color} w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0`}>
          <StatusIcon className="w-5 h-5 text-white" />
        </div>

        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-white font-semibold truncate">{solicitud.business_name}</h4>
            <span className="text-xs px-2 py-0.5 bg-gray-700 rounded-full text-gray-300">
              {BUSINESS_TYPES[solicitud.business_type] || solicitud.business_type}
            </span>
          </div>
          <p className="text-gray-400 text-sm truncate">{solicitud.contact_name}</p>
        </div>

        {/* Contact Quick Actions */}
        <div className="hidden md:flex items-center gap-2">
          <a 
            href={`tel:${solicitud.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="p-2 bg-gray-700 hover:bg-emerald-600 rounded-lg text-gray-300 hover:text-white transition-colors"
            title="Llamar"
          >
            <Phone className="w-4 h-4" />
          </a>
          {solicitud.email && (
            <a 
              href={`mailto:${solicitud.email}`}
              onClick={(e) => e.stopPropagation()}
              className="p-2 bg-gray-700 hover:bg-emerald-600 rounded-lg text-gray-300 hover:text-white transition-colors"
              title="Email"
            >
              <Mail className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Date */}
        <div className="hidden lg:block text-right">
          <p className="text-gray-400 text-sm">{formatDate(solicitud.created_at)}</p>
        </div>

        {/* Expand Icon */}
        <div className="text-gray-400">
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-4 pb-4"
        >
          <div className="bg-gray-700/50 rounded-lg p-4 space-y-4">
            {/* Contact Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-gray-400 text-xs mb-1">Teléfono</p>
                <a href={`tel:${solicitud.phone}`} className="text-white hover:text-emerald-400 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {solicitud.phone}
                </a>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Email</p>
                {solicitud.email ? (
                  <a href={`mailto:${solicitud.email}`} className="text-white hover:text-emerald-400 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {solicitud.email}
                  </a>
                ) : (
                  <span className="text-gray-500">No proporcionado</span>
                )}
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Fuente</p>
                <p className="text-white">{solicitud.source || 'webs-demo'}</p>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-gray-400 text-xs mb-1">¿Tiene web?</p>
                <p className="text-white">{solicitud.has_website === 'yes' ? 'Sí' : solicitud.has_website === 'improve' ? 'Sí, quiere mejorarla' : 'No'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Objetivo principal</p>
                <p className="text-white">
                  {solicitud.main_goal === 'calls' ? 'Más llamadas' :
                   solicitud.main_goal === 'bookings' ? 'Más reservas' :
                   solicitud.main_goal === 'seo' ? 'Aparecer en Google' : 'Todo'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Fecha solicitud</p>
                <p className="text-white">{formatDate(solicitud.created_at)}</p>
              </div>
            </div>

            {/* Quotes History */}
            {solicitud.quotes && solicitud.quotes.length > 0 && (
              <div>
                <p className="text-gray-400 text-xs mb-2">Presupuestos enviados</p>
                <div className="space-y-2">
                  {solicitud.quotes.map((quote, idx) => (
                    <div key={idx} className="bg-gray-800 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white font-semibold">{quote.amount}€</p>
                        <p className="text-gray-400 text-xs">{formatDate(quote.sent_at)}</p>
                      </div>
                      {quote.payment_link && (
                        <a 
                          href={quote.payment_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1 text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ver enlace
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-600">
              <select
                value={solicitud.status}
                onChange={(e) => onUpdateStatus(solicitud.id, e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
              >
                {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              
              <button
                onClick={() => onSendQuote()}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm"
              >
                <Send className="w-4 h-4" />
                Enviar Presupuesto
              </button>

              {/* Deploy Site Button */}
              {solicitud.status !== 'converted' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeploySite();
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
                  data-testid={`deploy-site-${solicitud.id}`}
                >
                  <Rocket className="w-4 h-4" />
                  Desplegar Sitio
                </button>
              )}

              {solicitud.status === 'converted' && solicitud.client_site_id && (
                <span className="flex items-center gap-2 px-4 py-2 bg-emerald-900/50 text-emerald-400 rounded-lg text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Sitio Desplegado
                </span>
              )}
              
              <a
                href={`https://wa.me/34${solicitud.phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${solicitud.contact_name}, gracias por tu interés en crear una web para ${solicitud.business_name}. ¿Tienes un momento para comentarte nuestra propuesta?`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
              >
                <MessageSquare className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default AdminWebsSolicitudesPanel;
